import * as dte from "../../../DtEventTypes";
import {FacetTypes, FacetsScope} from "../../../facets/Facets";
import {HudLayer} from '../HudLayer';
import {HUD_LAYER} from "../HudLayerConstants";
import {PRIORITY} from "../../LabelCompositor";
import { DtConstants } from "../../../schema/DtConstants";
import {ElementFlags, KeyFlags} from "../../../schema/dt-schema";
import {TandemCategories} from "../../../schema/TandemCategories";
import { COLOR_SCHEME } from "../../../streams/StreamLabelColors";
import {nowSec} from "../../../streams/timeline/time";
import {StreamUtils} from "../../../streams/StreamUtils";
import {PinUI} from "../../ui/PinUI";
import {DispatchEventOnHover, FocusOnHover, MouseInteractionsPlugin, SelectOnClick, UiCollision} from "../../ui/plugins";
import {AnimatedWorldPos3D} from "../../ui/positions";
import { ALERT_ICON, TICKET_ICON, NotificationDotUI } from "../../ui/NotificationDotUI";
import { TraceDragHandleUI } from "../../ui/TraceDragHandleUI";
import { getResourceUrl } from "../../../../globals";

const { ChangeTypes, StreamStates, StreamStatePriority, StreamAlertState2State, TicketPriority2Index } = DtConstants;

const TRACE_ELEMENT_TYPE = Object.freeze({
	START: 3,
	END: 2,
	POI: 1,
	DEFAULT: -1, // On path, but not start/end/POI
	// 0 is reserved for off-path elements (not explicitly used)
});

const connStatesEqual = (a, b) => {
	if (a === b) return true;
	if (a?.length !== b?.length) return false;
	return a.every((s) => b.includes(s));
};

// Note: We almost always want to sort in descending order, so the first element is the highest priority/state.
export const compareToTicket = (a, b) => (TicketPriority2Index[b?.priority] ?? -1) - (TicketPriority2Index[a?.priority] ?? -1);
export const compareToAlert = (a, b) => (b?.alertState ?? -1) - (a?.alertState ?? -1);

export const DEFAULT_TICKET_COLOR = '#7960FF';
export function getMostFrequentCustomThemeColor(tickets, defaultModel) {
    let resultColor;
    const countsByColor = {}
    let maxCount = 0;
    for (const t of tickets) {
        const c = defaultModel.myData.dbId2ThemingColor?.[t.dbId];
        if (c) {
            const n = (countsByColor[c] = (countsByColor[c] || 0) + 1);
            if (n > maxCount) { maxCount = n; resultColor = c; }
        }
    }

    return resultColor;
}

export class AssetsLayer extends HudLayer {
    /** @type{DtMap} Elements with a label, union of billable Assets and parents of connections/tickets. */
    filteredElements = new DtMap();
    labelById = new DtMap();
    connStateByLabel = new DtMap();
    ticketsByLabel = new DtMap();
    alertsByLabel = new DtMap();
    priorities = Object.keys(PRIORITY).map(() => ([]));
    #facilityLevelItem = null;

    hasDirtyReadings = false;
    hasDirtyTickets = false;
    needsRedraw = false;

    /** @type {Set<number>} Limits elements by Tandem Category index. Missing means allow any. */
    filteredTCs;
    /** @type {string[]|} Connection states to show, if any. Undefined/null means show all */
    connStates;
    isAssetsVisible = true;
    isTicketsVisible = true;

	// Points to the same tracedElementTypeByModel Map in SystemVisualizer
	#tracedElementTypeByModel = null;
	#dragTarget = null;

    constructor(facility, hud, viewer) {
        super(HUD_LAYER.ASSETS.id, facility, hud, viewer);

        this.hasTrackingLabels = true;
        this.boundOnReadingsChanged = this.#onReadingsChanged.bind(this);
        this.boundOnThemingChanged = this.#onThemingChanged.bind(this);
        this.boundOnTicketsChanged = this.#onTicketsChanged.bind(this);
		this.boundOnTraceElementsChanged = this.#onTraceElementsChanged.bind(this);
		this.boundOnTraceDragStart = this.#onTraceDragStart.bind(this);
		this.boundOnTraceDragEnd = this.#onTraceDragEnd.bind(this);
    }

    dispose() {
        this.labelById.forEach((l) => l.dispose());
        this.labelById.clear();
        this.connStateByLabel.clear();
        this.ticketsByLabel.clear();
        this.alertsByLabel.clear();

        this.facility.eventTarget.removeEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.streamMgr.view.removeEventListener(dte.DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.streamMgr.view.removeEventListener(dte.DT_TELEMETRY_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.eventTarget.removeEventListener(dte.DT_THEMING_CHANGED_EVENT, this.boundOnThemingChanged);
        this.facility.eventTarget.removeEventListener(dte.DT_TICKETS_CHANGED_EVENT, this.boundOnTicketsChanged);
		this.viewer.removeEventListener(dte.DT_TRACE_ELEMENTS_CHANGED_EVENT, this.boundOnTraceElementsChanged);
		this.viewer.removeEventListener(dte.DT_TRACE_DRAG_BEGIN, this.boundOnTraceDragStart);
		this.viewer.removeEventListener(dte.DT_TRACE_DRAG_STOP, this.boundOnTraceDragEnd);
    }

    init() {
        this.facility.eventTarget.addEventListener(dte.DT_STREAMS_INFO_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.streamMgr.view.addEventListener(dte.DT_TELEMETRY_INSTANT_READINGS_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.streamMgr.view.addEventListener(dte.DT_TELEMETRY_CHANGED_EVENT, this.boundOnReadingsChanged);
        this.facility.eventTarget.addEventListener(dte.DT_THEMING_CHANGED_EVENT, this.boundOnThemingChanged);
        this.facility.eventTarget.addEventListener(dte.DT_TICKETS_CHANGED_EVENT, this.boundOnTicketsChanged);
		this.viewer.addEventListener(dte.DT_TRACE_ELEMENTS_CHANGED_EVENT, this.boundOnTraceElementsChanged);
		this.viewer.addEventListener(dte.DT_TRACE_DRAG_BEGIN, this.boundOnTraceDragStart);
		this.viewer.addEventListener(dte.DT_TRACE_DRAG_STOP, this.boundOnTraceDragEnd);

        return this;
    }

    #isConnStateVisible(state) {
        if (!state) return false;
        if (!this.connStates) return true;
        return this.connStates.includes(state);
    }

    #onThemingChanged({ scope }) {
        if (scope !== FacetsScope.Tickets) return;

        // force a re-compose to get new colors for tickets and so that deferred counts get them
        this.needsRedraw = true;
        this.invalidate();
    }

    #onTicketsChanged() {
        this.hasDirtyTickets = true;
        this.#updateFilteredElements()
            .then(() => { this.needsRedraw = true; });
    }

    onClusterTransition(type, layerPrefs, isClustered) { /* Do nothing and allow labels. */ }

    onIsolationChanged() { this.#updateFilteredElements(); }

    onSelectionChanged() {
        const defaultModel = this.facility.getDefaultModel();
        let hasChanged = false;
        for (const label of this.labelById.values()) {
            const { model, dbId, tickets } = label.data;
            let selected = Boolean(model.selector?.isSelected(dbId));
            if (!selected && tickets && defaultModel) {
                selected = tickets.some((t) => defaultModel.selector?.isSelected(t.dbId));
            }

            if (label.isSelected !== selected) {
                hasChanged = true;
                label.setSelected(selected);
            }
        }

        if (hasChanged) this.invalidate();
    }

    async setVisible(visible) {
        const then = super.setVisible(visible);

        if (visible) {
            // immediately update elements for accurate assets while animating to full visibility.
            this.hasDirtyReadings = true;
            this.hasDirtyTickets = true;
            await this.#updateFilteredElements();
        } else {
            let hasVisibleLabels = false;
            for (const ui of this.labelById.values()) {
                if (ui.isVisible()) {
                    hasVisibleLabels = true;
                    break;
                }
            }

            if (!hasVisibleLabels) {
                // No reason to animate, just skip to the end.
                this.visibility.param.skipAnim();
            }

            // Re-compose just after turning invisible, to cause removal of deferred assets in other visible layers.
            await then;
            this.needsRedraw = true;
            this.invalidate();
        }

        return then;
    }

    setState(prefs) {
        let changed = false;

        if (prefs.assets != null && this.isAssetsVisible !== prefs.assets) {
            this.isAssetsVisible = prefs.assets;
            changed = true;
        }

        if (prefs.connStates != null) {
            if (!connStatesEqual(prefs.connStates, this.connStates)) {
                this.connStates = prefs.connStates;
                changed = true;
            }
        } else if (this.connStates) {
            // default case - show all stream states
            this.connStates = null;
            changed = true;
        }

        if (prefs.tickets != null && this.isTicketsVisible !== prefs.tickets) {
            this.isTicketsVisible = prefs.tickets;
            changed = true;
        }

        // undefined -> no change, null -> clear filter, array -> set filter. State is saved as code not index.
        let filteredTCs = prefs.filteredTCs;
        if (filteredTCs !== undefined) {

            // Normalize incoming filter to Set of indexes or default.
            if (filteredTCs) {
                const indexes = new Set();
                for (const c of filteredTCs) {
                    const index = TandemCategories.code2index(c);
                    if (index > 0) indexes.add(index);
                }
                filteredTCs = indexes.size === TandemCategories.size ? null : indexes;
            }

            // Use default of all categories (assumes incoming state is unique)
            if (filteredTCs === null) {
                if (this.filteredTCs) {
                    this.filteredTCs = null;
                    changed = true;
                }
            } else {
                // Compare specific filters.
                if (!this.filteredTCs
                    || this.filteredTCs.size !== filteredTCs.size
                    || !this.filteredTCs.values().every((v) => filteredTCs.has(v))) {

                    // Setting a new filtered set.
                    this.filteredTCs = filteredTCs;
                    changed = true;
                }
            }
        }

        if (changed) {
            this.#updateFilteredElements();
        }

        super.setState(prefs);
    }

    getState() {
        const state = super.getState();
        state.tickets = this.isTicketsVisible;
        if (this.connStates) state.connStates = this.connStates;

        // Saving as codes and using missing as default to allow reloading a configuration after a change to TCs.
        if (this.filteredTCs) state.filteredTCs = Array.from(this.filteredTCs, (i) => TandemCategories.index2code(i));

        return state;
    }

    compose(ctx, priority) {
        // needsRedraw will be cleared by HudLayers, no need to do it here.
        const uis = this.priorities[priority];

        if (priority === PRIORITY.OFF) {
            for (const ui of uis) {
                ui.setVisible(false);
            }
            return;
        }

        if (priority === PRIORITY.NORMAL && this.#facilityLevelItem) {
            ctx.rollups.addDeferred(this.#facilityLevelItem);
        }

        if (!uis.length) return;

        // Main composition extracted for testing purposes.
        doComposeAssetUIs(ctx, priority, uis);
    }

    update(ts) {
        if (!this.isVisible()) return;

        // Reset priority arrays
        this.priorities.forEach((uis) => { uis.length = 0; });

        const mark = (lbl, priority) => {
            this.priorities[priority].push(lbl);
            if (lbl.data.priority === priority) return;

            lbl.data.priority = priority;
        }

        const defaultModel = this.facility.getDefaultModel();
        const toRemove = this.labelById.clone();
        const themedFacetId = this.facility.facetsManager.getThemedFacetDef(FacetsScope.Tickets)?.id;
        const isSingleElementTrace = this.#tracedElementTypeByModel?.size === 1 && this.#tracedElementTypeByModel.values().next().value.size === 1;

        for (const [model, byDbId] of this.filteredElements.map) {
            const it = model.getInstanceTree();

            for (const [dbId, metadata] of byDbId) {
                const { tickets, alerts, connState, isSpatial, tracedType } = metadata;

                // no need to remove.
                toRemove.delete(model, dbId);

                let label = this.labelById.get(model, dbId);
                if (!label) {
                    label = new ElementUI(this.hud, this.layerID, model, dbId);

                    // even if asset labels for location will always be deferred, we use the mechanisms of the label
                    // to hold and manage metadata to be rolled up. This can be optimized later, but at least for now
                    // we disconnect it from the DOM to remove unnecessary DOM re-render / re-styling.
                    if (!isSpatial) label.init(this.dom);

                    let selected = Boolean(model.selector?.isSelected(dbId));
                    if (!selected && tickets && defaultModel) {
                        selected = tickets.some((t) => defaultModel.selector?.isSelected(t.dbId));
                    }

                    label.setSelected(selected);
                    this.labelById.set(model, dbId, label);
                }

                // here all metadata is relevant to the UI label (alert, tickets, connState, room & level facets)
                Object.assign(label.data, metadata);

                // Prevent traced elements from being hidden
                if (!tracedType && it.isNodeHidden(dbId)) {
                    mark(label, PRIORITY.OFF);
                    continue;
                }

                connState ? label.updateConnectionState(connState) : label.clearConnectionState();

                tickets ? label.notifyTickets(tickets, themedFacetId) : label.clearTicket();

                alerts ? label.notifyAlerts(alerts) : label.clearAlert();

                const showTraceHandle = !isSpatial && !isSingleElementTrace && tracedType >= TRACE_ELEMENT_TYPE.END;
                showTraceHandle ? label.notifyTraceDragHandle(tracedType) : label.clearTraceDragHandle();

                if (label.isSelected || tracedType) {
                    // If the label is selected or is part of a trace, it should be on top.
                    mark(label, PRIORITY.TOPMOST);
                } else {
                    mark(label, (tickets || alerts) ? PRIORITY.ABOVE : PRIORITY.NORMAL);
                }
            }
        }

        // Cleanup unseen label
        toRemove.forEach((label, model, dbId) => {
            label.dispose();
            this.labelById.delete(model, dbId);
        })

        // Since there's still some relative difference in importance between NORMAL labels, we sort them.
        // at the moment, re-sorting within the same priority doesn't trigger a redraw.
        this.priorities[PRIORITY.NORMAL].sort(byDescConnectionState);

        this.isDirty = false;

        // Some changes we make will directly affect layer composition (ex: label priority) and some will indirectly
        // affect how deferred labels are shown (ex: connection state, tickets, alerts). So while the UI for the asset
        // label is immediately updated, for now, we force a re-compose to update the deferred labels in other layers.
        // note: it's quicker to redraw than diffing all metadata since most typical updates will require a redraw.
        return true;
    }

    uis() {
        return this.labelById.values();
    }

    #getMetadataForElement(model, dbId) {
        let metadata;

        const tickets = this.#getVisibleTicketsForHost(model, dbId);
        if (this.isTicketsVisible && tickets) metadata = { tickets };

        const allAlerts = this.alertsByLabel.get(model, dbId);
        if (allAlerts) {
            const alerts = allAlerts.filter((a) => this.#isConnStateVisible(StreamAlertState2State[a.alertState]));
            if (alerts.length) (metadata ?? (metadata = {})).alerts = alerts;
        }

        const connState = this.connStateByLabel.get(model, dbId);
        if (connState && this.#isConnStateVisible(connState.state)) {
            (metadata ?? (metadata = {})).connState = connState;
        }

        return metadata;
    }

    #updateFacilityLevelItem() {
        const model = this.facility.getDefaultModel();
        if (!model) { this.#facilityLevelItem = null; return; }

        if (this.filteredTCs && !this.filteredTCs.has(TandemCategories.Facility)) {
            this.#facilityLevelItem = null;
            return;
        }

        const dbId = model.getRootId();
        const metadata = this.#getMetadataForElement(model, dbId);
        this.#facilityLevelItem = metadata ? { model, dbId, isSpatial: true, ...metadata } : null;
    }

    onModelChanged({ change }) {
        const defaultModelId = this.facility.getDefaultModelId();
        const { ctype, modelId, details } = change;

        switch (ctype) {
            case ChangeTypes.Mutate:
                if (modelId === defaultModelId) {
                    // #onTicketsChanged will handle ticket-only mutations
                    if (change.dbIds && change.dbIds.length > 0) {
                        const defaultModel = this.facility.getDefaultModel();
                        const dbId2flags = defaultModel.getData().dbId2flags;
                        const isChangeOnlyTickets = dbId2flags && change.dbIds.every(dbId =>
                            dbId2flags[dbId] === ElementFlags.Ticket
                        );

                        if (isChangeOnlyTickets) return;
                    }
                }
                this.#updateFilteredElements();
                return;
            case ChangeTypes.StreamAlert:
                if (modelId === defaultModelId && details.alertState !== details.previousState) {
                    this.#onAlertChanged(details);
                }
                return;
            default:
                return;
        }
    }

    #onAlertChanged({ alertState, streamId, substreamEncId }) {
        if (!this.isVisible()) return;

        const stream = this.facility.streamMgr.getAllStreamInfosFromCache().find((s) => s.fullId === streamId);
        if (stream?.hostElement == null) return;

        const { model, hostId } = stream.hostElement;
        const alerts = this.alertsByLabel.get(model, hostId);
        let updatedAlerts;

        // Assets had no prior active alerts.
        if (!alerts) {
            if (alertState > 0) {
                // First alert for this asset
                updatedAlerts = [{ alertState, streamId, substreamEncId }];
            } else {
                return;
            }
        } else {
            const priorIdx = alerts.findIndex((a) => a.streamId === streamId && a.substreamEncId === substreamEncId);
            if (priorIdx >= 0) {
                // Host has other alerts, but never for this stream.
                if (alertState > 0) {
                    // update in place
                    alerts[priorIdx].alertState = alertState;
                    updatedAlerts = alerts.sort(compareToAlert);
                } else {
                    // removing an element preserved the order.
                    alerts.splice(priorIdx, 1);
                    updatedAlerts = alerts.length ? alerts : null; // clear if no more alert
                }
            } else {
                // Host has other alerts, but never for this stream.
                if (alertState > 0) {
                    alerts.push({ alertState, streamId, substreamEncId });
                    updatedAlerts = alerts.sort(compareToAlert);
                } else {
                    return;
                }
            }
        }

        // Inner alert structure
        this.alertsByLabel.set(model, hostId, updatedAlerts);

        if (model === this.facility.getDefaultModel() && hostId === model.getRootId()) {
            this.#updateFacilityLevelItem();
        }

        const visibleAlerts = updatedAlerts?.filter((a) => this.#isConnStateVisible(StreamAlertState2State[a.alertState]));
        const alertsToShow = visibleAlerts?.length ? visibleAlerts : null;

        // Metadata for assets
        const metadata = this.filteredElements.get(model, hostId);
        const label = this.labelById.get(model, hostId);

        // Call #updateFilteredElements if the element is currently not shown or should no longer be shown,
        // such as when the alert state was visible previously, but not anymore after and vice versa.
        if (alertsToShow) {
            if (!metadata) {
                this.#updateFilteredElements();
                return;
            }
            metadata.alerts = alertsToShow;
            if (label) {
                label.data.alerts = alertsToShow;
                label.notifyAlerts(alertsToShow)
            }
        } else {
            if (metadata?.alerts) {
                this.#updateFilteredElements();
                return;
            }
            if (label) {
                label.clearAlert();
            }
        }

        // Any change of alert state would be visible in the UI (either on the asset or its rollup).
        this.needsRedraw = true;
    }

    async #updateTickets() {
        if (!this.isVisible()) return;

        const defaultModel = this.facility.getDefaultModel();
        if (!defaultModel) return;

        const tickets = await defaultModel.getTickets();

        this.ticketsByLabel.clear();

        for (const ticket of tickets) {
            if (!ticket.hosts?.length) continue;

            for (const hostElement of ticket.hosts) {
                const hostTickets = this.ticketsByLabel.getOrSet(hostElement.model, hostElement.hostId, []);
                hostTickets.push(ticket);
            }
        }

        this.ticketsByLabel.forEach((hostTickets) => hostTickets.sort(compareToTicket));

        this.hasDirtyTickets = false;
    }

	#getVisibleTicketsForHost(model, hostId) {
		const defaultModel = this.facility.getDefaultModel();
		const tickets = this.ticketsByLabel.get(model, hostId);
		if (!defaultModel || !tickets) return;

		const visible = tickets.filter((t) => defaultModel.visibilityManager.isNodeVisible(t.dbId));
		return visible.length ? visible : null;
	}

    async #onReadingsChanged({ updated, instant }) {
        if (!this.isVisible()) return;

        if (!updated) {
            // if details on what was updated are missing, recalculate everything.
            this.hasDirtyReadings = true;
            return this.#updateFilteredElements();
        }

        const streams = this.facility.streamMgr.getAllStreamInfosFromCache();
        const toUpdate = new DtMap();

        // the IOT changes are most likely to a single <conn, attr> pair, but that might change in the future.
        // we need 2 passes since we don't have a Map<hostElement, streams[]>
        for (let i = 0; i < updated.length; i += 2) {
            const stream = streams.find((s) => s.dbId === updated[i]);
            if (stream?.hostElement) {
                const { model, hostId } = stream.hostElement;
                const prior = this.connStateByLabel.get(model, hostId);
                toUpdate.set(model, hostId, prior);
                this.connStateByLabel.delete(model, hostId);
            }
        }

        // Updates the per-host connection state, for each affected host element.
        const time = this.facility.streamMgr.view.time.resolve().unix();
        const commonNow = nowSec();
        for (let stream of streams) {
            if (!stream.hostElement) continue;

            const { hostId, model } = stream.hostElement;
            if (!toUpdate.get(model, hostId)) continue;

            const priorWorst = this.connStateByLabel.get(model, hostId);
            const state = StreamUtils.getConnectionStateFromInstantData(stream, instant, time, commonNow);

            if (!priorWorst || StreamStatePriority[state] > StreamStatePriority[priorWorst.state]) {
                this.connStateByLabel.set(model, hostId, { stream, state });
            }
        }

        // If stream states are explicitly hidden, we don't need to update the UIs
        if (this.connStates?.length === 0) return;

        let needsRebuild = false;
        let needsRedraw = false;
        toUpdate.forEach((prior, model, hostId) => {
            // Return early if we need a rebuild
            if (needsRebuild) return;

            const active = this.connStateByLabel.get(model, hostId);
            const showActive = this.#isConnStateVisible(active?.state);
            const priorShown = this.#isConnStateVisible(prior?.state);

            // already shown/hidden and the new status is still the same, no need to update
            if (prior?.state === active?.state && priorShown === showActive) return;

            const metadata = this.filteredElements.get(model, hostId);
            const label = this.labelById.get(model, hostId);

            if (model === this.facility.getDefaultModel() && hostId === model.getRootId()) {
                this.#updateFacilityLevelItem();
            }

            if (showActive) {
                if (!metadata) {
                    // set needsRebuild to true if the element is currently not shown or should no longer be shown,
                    // such as when the connection state was visible previously, but not anymore after and vice versa.
                    needsRebuild = true;
                    return;
                }
                metadata.connState = active;
                if (label) {
                    label.data.connState = active;
                    label.updateConnectionState(active);
                }
            } else {
                if (metadata?.connState) {
                    needsRebuild = true;
                    return;
                }
                if (label) {
                    label.clearConnectionState();
                }
            }
            // elements & labels are up-to-date, just update the deferred label through composition.
            needsRedraw = true;
        });

        if (needsRebuild) return this.#updateFilteredElements();
        if (needsRedraw) this.needsRedraw = true;
    }

    async #updateFromReadings() {
        const streamMgr = this.facility.streamMgr;
        const readings = await streamMgr.view.getInstantReadings();
        if (!readings) return;

        // Current time configuration
        const time = streamMgr.view.time.resolve().unix();
        const commonNow = nowSec();

        // Worst state by host {WARN, ALERT, OFFLINE}
        const streams = streamMgr.getAllStreamInfosFromCache();
        this.connStateByLabel.clear();

        // TEMP: Move to dedicated alert update handler
        const alerts = await streamMgr.getCurrentAlerts();
        this.alertsByLabel.clear();

        for (const stream of streams) {
            if (!stream.hostElement) continue;

            const { hostId, model } = stream.hostElement;

            // Determine the worst connection state for the host
            const priorWorst = this.connStateByLabel.get(model, hostId);
            const state = StreamUtils.getConnectionStateFromInstantData(stream, readings, time, commonNow);

            if (!priorWorst || StreamStatePriority[state] > StreamStatePriority[priorWorst.state]) {
                // First or higher priority state for the host
                this.connStateByLabel.set(model, hostId, { stream, state });
            }

            // Obtain alerts for hosted connections
            const alertByAttrId = alerts?.[stream.fullId];
            if (alertByAttrId) {
                const partialAlerts = this.alertsByLabel.getOrSet(model, hostId, []);
                for (const attrId in alertByAttrId) {
                    const { currentState } = alertByAttrId[attrId];
                    if (StreamAlertState2State[currentState] === StreamStates.Normal) continue; // skip Normal
                    partialAlerts.push({ alertState: currentState, streamId: stream.fullId, substreamEncId: attrId });
                }
            }
        }

        // Sort once we know all possible alerts for a host.
        this.alertsByLabel.forEach((alerts) => alerts.sort(compareToAlert));

        this.hasDirtyReadings = false;
    }

    #onTraceElementsChanged({ tracedElementTypeByModel }) {
        this.#tracedElementTypeByModel = tracedElementTypeByModel;
        this.#updateFilteredElements();
    }

    * iterateElements() {
        for (const byModel of this.facility.facetsManager.idsByModelFiltered || []) {
            yield byModel;
        }

        if (this.#tracedElementTypeByModel) {
            // idsByModelFiltered includes all elements that passed through the facet filters,
            // but some elements in the traces may have been filtered out since traces are displayed regardless of
            // facet filters. We need to also iterate over tracedElementTypeByModel to get all elements that can be labeled
            for (const [model, byDbId] of this.#tracedElementTypeByModel) {
                yield [model, byDbId.keys()];
            }
        }
    }

    async #updateFilteredElements() {
        if (!this.isVisible()) return;

        const dependencies = [];
        if (this.hasDirtyReadings) dependencies.push(this.#updateFromReadings());
        if (this.hasDirtyTickets) dependencies.push(this.#updateTickets());
        await Promise.all(dependencies);

        //console.time('AssetsLayer#updateFilteredElements :');
        this.filteredElements.clear();
        for (const [model, dbIds] of this.iterateElements()) {
            const data = model.getData();
            if (!data) continue;

            const byDbId = this.filteredElements.map.get(model) || new Map();
            for (const dbId of dbIds) {
                // Skip if already processed
                if (byDbId.has(dbId)) continue;

                const flags = data.dbId2flags[dbId];

                // Spatial elements can still be assets, but will never show as an asset label (only deferred).
                const isSpatial = flags === ElementFlags.Room || flags === ElementFlags.Level || flags === ElementFlags.Location;
                if (!isSpatial && (flags & ElementFlags.AllLogicalMask) !== KeyFlags.Physical) {
                    // Skip logical elements (connection/tickets/generic)
                    continue;
                }

                // Ensure labels are shown for trace start/end/POI elements
                // For other traced elements, we use the same logic as for non-traced elements
                const tracedType = this.#tracedElementTypeByModel?.get(model)?.get(dbId);
                const canBeFiltered = !tracedType || tracedType <= TRACE_ELEMENT_TYPE.POI;

                if (canBeFiltered && this.filteredTCs && !this.filteredTCs.has(data.dbId2TandemCategory[dbId])) {
                    // The Tandem Category filter applies on both assets or host of connections/alerts/tickets.
                    continue;
                }

                let metadata = this.#getMetadataForElement(model, dbId);

                const needsTraceLabel = tracedType >= TRACE_ELEMENT_TYPE.POI;

                // later code doesn't care about strict assets, but if it does, we could add a differentiator here.
                if (this.isAssetsVisible && !metadata && (needsTraceLabel || model.isAsset(dbId))) metadata = {};

                if (metadata) {
                    metadata.isSpatial = isSpatial;
                    metadata.tracedType = tracedType;
                    byDbId.set(dbId, metadata);
                }
            }

            if (byDbId.size) {
                this.filteredElements.map.set(model, byDbId);
            }
        }
        //console.timeEnd('AssetsLayer#updateFilteredElements :');

        const options = {
            modelsByUrn: this.facility.facetsManager.models.reduce((dict, m) => { dict[m.urn()] = m; return dict; }, {})
        };
        const levelsDef = this.facility.facetsManager.getFacetDef(FacetTypes.levels);
        const levelFacets = levelsDef.batchClassify(this.filteredElements.asIdSetIterator(), options);
        for (const { id, ids } of levelFacets.facets) {
            for (const [model, dbIds] of ids) {
                const byDbId = this.filteredElements.map.get(model);
                for (const dbId of dbIds) {
                    const metadata = byDbId.get(dbId);
                    metadata.levelsFacetId ??= [];
                    metadata.levelsFacetId.push(id);
                }
            }
        }

        const spacesDef = this.facility.facetsManager.getFacetDef(FacetTypes.spaces);
        const spaceFacets = spacesDef.batchClassify(this.filteredElements.asIdSetIterator(), options);
        for (const { id, ids } of spaceFacets.facets) {
            for (const [model, dbIds] of ids) {
                const byDbId = this.filteredElements.map.get(model);
                for (const dbId of dbIds) {
                    const metadata = byDbId.get(dbId);
                    metadata.spacesFacetId ??= [];
                    metadata.spacesFacetId.push(id);
                }
            }
        }

        this.#updateFacilityLevelItem();
        this.invalidate();
    }

	// Ideally this should be coming from plugins as "OnTraceHover"
	// but right now it's inverted since the event comes from the ToolController without going through the plugins
	// eg. ToolController -> HUD -> AssetsLayer -> ElementUI (trace label) -> DispatchEvent
	handleMouseMove(event) {
		if (this.#dragTarget) {
			this.#dragTarget.dragMove(event);
			return true; // stop propagation
		}
	}

	#onTraceDragStart(event) {
		this.#dragTarget = this.labelById.get(event.model, event.dbId);
		this.labelById.delete(event.model, event.dbId);

		this.invalidate();
	}

	#onTraceDragEnd(event) {
		this.#dragTarget?.dispose();
		this.#dragTarget = null;

		this.invalidate();
	}
}

export function doComposeAssetUIs(ctx, priority, uis) {
    // Since UIs can be colliding without being in the same rollup facet, we still perform composition
    // in order of priority, so higher priority UIs have less chance of being deferred than lower priority UIs.
    for (const ui of uis) {
        // if the UI location(s) have been pulled back, or if the asset itself is a location.
        if (ui.data.isSpatial || (ctx.rollups.shouldBeDeferred(ui.data) && priority !== PRIORITY.TOPMOST)) {
            ctx.rollups.addDeferred(ui.data);
            ui.setVisible(false);
            continue;
        }

        const target = ui.getCollisionTarget();
        // Mark the target as available to be pulled back to a rollup if a future target requests it.
        target.withPullback = priority !== PRIORITY.TOPMOST;
        if (!ctx.onScreen(target)) {
            // Being offscreen isn't enough to defer a label, but they still can be.
            ctx.rollups.addOffscreen(target);
            ui.setVisible(false);
            continue;
        }

        // As soon as we place it, the compositor manages visibility to retain the ability to pull back UIs.
        ctx.placeAsset(target);
    }
}

/**
 * This callback is displayed as a global member.
 * @callback ForeachCallbackDtMap
 * @param {any} item
 * @param {DtModel} model
 * @param {number} dbId
 */

const _map = new Map();
class DtMap {
    constructor() { this.map = new Map(); }

    /** Creates a shallow iterator for the idSet described by the keys in this map. */
    asIdSetIterator() {
        _map.clear();
        for (const [model, dbIds] of this.map) {
            _map.set(model, dbIds.keys());
        }
        return _map;
    }

    /** Clears map */
    clear() { this.map.clear(); }

    /** Shallow clone key-entries maps */
    clone() {
        const tmp = new DtMap();
        for (const [model, dbIds] of this.map) {
            tmp.map.set(model, new Map(dbIds));
        }
        return tmp;
    }

    /** Remove entry */
    delete(model, dbId) {
        const byDbId = this.map.get(model);
        if (byDbId && byDbId.has(dbId)) {
            byDbId.delete(dbId);
        }
    }

    /** Get element from compound key */
    get(model, dbId) {
        return this.map.get(model)?.get(dbId);
    }

    /** Get element and set parameter if missing */
    getOrSet(model, dbId, orSet) {
        let byDbId = this.map.get(model);
        if (!byDbId) this.map.set(model, byDbId = new Map());

        let item = byDbId.get(dbId);
        if (item == null) byDbId.set(dbId, item = orSet);

        return item;
    }

    /**
     * Foreach entries, vaguely matches Map.forEach
     * @param {ForeachCallbackDtMap} foreachFn
     */
    forEach(foreachFn) {
        for (const [model, byDbId] of this.map) {
            for (const [dbId, item] of byDbId) {
                foreachFn(item, model, dbId);
            }
        }
    }

    /** Set entry by compound key */
    set(model, dbId, item) {
        let byDbId = this.map.get(model);
        if (!byDbId) this.map.set(model, byDbId = new Map());

        byDbId.set(dbId, item);
        return this;
    }

    /** Foreach values, vaguely matches Map.values */
    * values() {
        for (const byDbId of this.map.values()) {
            yield* byDbId.values();
        }
    }
}

export function getStateColor(state) {
    switch (state) {
        case StreamStates.Alert:
            return COLOR_SCHEME.error;
        case StreamStates.Warning:
            return COLOR_SCHEME.warning;
        case StreamStates.Normal:
            return COLOR_SCHEME.success;
        case StreamStates.NoData:
        case StreamStates.Offline:
            return '#999'; // Temporarily switch to Figma offline color for higher contrast.
        //return COLOR_SCHEME.offline;
        default:
            console.warn('unexpected state for asset label', state);
    }
}

function byDescConnectionState(a, b) {
    if (!a.data?.connState) return 1;
    if (!b.data?.connState) return -1;
    return StreamStatePriority[b.data.connState.state] - StreamStatePriority[a.data.connState.state];
}

const ESTIMATED_SIZE = { width: 40, height: 39 };
const ESTIMATED_HEIGHT_OFFSET = 8; // half of the height of trace drag handle
export class ElementUI extends PinUI {
    static spriteUrl;

    constructor(hud, layer, model, dbId) {
        ElementUI.spriteUrl ??= getResourceUrl('res/ui/tandem-cats.svg');

        const tandemCategory = model.getElementTandemCategory(dbId);
        super(hud, layer,
            new AnimatedWorldPos3D(model, dbId),
            tandemCategory && `<use href="${ElementUI.spriteUrl}#${tandemCategory}" fill="black" height="12" width="12" transform="translate(-6,-6)"/>`);

        this.data.dbId = dbId;
        this.data.model = model;

		const it = model.getInstanceTree();
		const name = it.getNodeName(dbId);
		this.innerDom.setAttribute('data-test-id', `name:${name}`);

        this.eventTarget = this.iconHost;
        this.plugins.push(new UiCollision());
		this.plugins.push(new MouseInteractionsPlugin());
        this.plugins.push(new SelectOnClick());
        this.plugins.push(new DispatchEventOnHover());
		this.plugins.push(new FocusOnHover());
    }

    notifyTraceDragHandle(tracedType) {
        this.traceDragHandle ??= new TraceDragHandleUI(this);
        this.traceDragHandle.updateTracedType(tracedType);
        this.traceDragHandle.setActive(true);
    }

    clearTraceDragHandle() {
        this.traceDragHandle?.setActive(false);
    }

    updateConnectionState({ state }) {
        // Color is solely based on worst stream state, if applicable
        this.setBGColor(getStateColor(state));
    }

    clearConnectionState() {
        this.data.connState = null; // the Object.assign won't clear
        this.setBGColor('');
    }

    notifyTickets(tickets, currentThemedFacetId) {
        const onTicketDotClick = () => {
            this.hud.viewer.dispatchEvent({
                type: dte.DT_SHOW_ASSET_DETAIL_EVENT,
                model: this.data.model,
                dbId: this.data.dbId,
                notificationType: 'tickets',
            });
        };
        this.ticketDot ??= new NotificationDotUI(this, TICKET_ICON, { top: 'top', left: 'left' }, onTicketDotClick);
        this.ticketDot.setCount(tickets.length);

        // use custom themed color to display tickets if there is one, otherwise default to priority color
        // in the case multiple tickets, we use the highest priority color (if colored by priority) or the most frequent custom theme color
        if (tickets.length) {
            const defaultModel = this.data.model.facility.getDefaultModel();
            let ticketDotColor = tickets[0].priorityColor;
            if (currentThemedFacetId) {
                ticketDotColor = defaultModel.myData.dbId2ThemingColor?.[tickets[0].dbId];
                if (currentThemedFacetId !== FacetTypes.priority) {
                    ticketDotColor = getMostFrequentCustomThemeColor(tickets, defaultModel);
                }
            }
            this.ticketDot.setBGColor(ticketDotColor ?? DEFAULT_TICKET_COLOR);
        }
    }

    clearTicket() {
        this.data.tickets = null; // the Object.assign won't clear
        this.ticketDot?.setCount(0);
    }

    notifyAlerts(alerts) {
        const onAlertDotClick = () => {
            this.hud.viewer.dispatchEvent({
                type: dte.DT_SHOW_ASSET_DETAIL_EVENT,
                model: this.data.model,
                dbId: this.data.dbId,
                notificationType: 'alerts',
            });
        };
        const state = alerts[0].alertState;
        const count = alerts.length;
        const color = getStateColor(StreamAlertState2State[state]);

        this.alertDot ??= new NotificationDotUI(this, ALERT_ICON, { top: 'top', left: 'right' }, onAlertDotClick);
        this.alertDot.setBGColor(color);
        this.alertDot.setCount(count);
    }

    clearAlert() {
        this.data.alerts = null; // the Object.assign won't clear
        this.alertDot?.setCount(0);
    }

	dragStart() {
        this.clearAlert();
        this.clearTicket();
        this.clearConnectionState();
        this.hideIcon();

		const { dbId, model } = this.data;
		this.hud.viewer.dispatchEvent({ type: dte.DT_TRACE_DRAG_BEGIN, dbId, model });
	}

	dragMove(event) {
		// Check if the left mouse button is still pressed and stop dragging if it's not
		if (event.buttons !== 1) {
			this.dragStop();
			return;
		}

		// Move the label to the mouse position, aligned with the trace drag handle
		const viewer = this.hud.viewer;
		viewer.toolController.__clientToCanvasCoords(event);
		this.onCanvas2DChange({ x: event.canvasX, y: event.canvasY - ESTIMATED_HEIGHT_OFFSET });

		// Get the dbId and model at the mouse position
		const vImpl = viewer.impl;
		const idRes = [];
		const vpVec = vImpl.clientToViewport(event.canvasX, event.canvasY);
		const dbId = vImpl.renderer().idAtPixel(vpVec.x, vpVec.y, idRes);
		const model = vImpl.modelQueue().findModel(idRes[1]);

		if (dbId && model) {
			viewer.dispatchEvent({ type: dte.DT_TRACE_DRAG_HOVER, dbId, model });
		}
	}

	dragStop() {
		this.hud.viewer.dispatchEvent({ type: dte.DT_TRACE_DRAG_STOP });
	}

    getEstimatedSize() { return ESTIMATED_SIZE; }
}
